import { useMemo } from 'react'
import { Input, Checkbox, Button, Autocomplete } from '@fullscreendigital/carrefour-dex-fe'
import { Modal } from 'components/core-ui/Modal'
import { useFormik } from 'formik'
import { getInitialValues, getValidationSchema, getManagerValidationSchema } from './helpers'
import { useNotification } from 'hooks'
import { formatErrorMessage } from 'utils/formatting'
import { CircularProgress, Grid } from '@mui/material'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import { makeCoreGetRequest, makeCorePatchRequest, makeCorePostRequest } from 'utils/api'
import { useMutation, useQuery } from 'react-query'
import { FormInfoItems } from 'components/core-ui/FormInfoItems'

interface NewAdminDepositProps {
  isOpen: boolean
  closeModal: Function
  isEdit?: boolean
  editItem?: AdminStore
  shopCodes: AutocompleteOption[]
  metiVersions: AutocompleteOption[]
  parentSocieties: AutocompleteOption[]
  metiShops: AutocompleteOption[]
  setShouldRefreshData: Function
  permissions: Permissions
}

const AdminStoreForm: React.FC<NewAdminDepositProps> = props => {
  const {
    isOpen,
    closeModal,
    isEdit,
    editItem,
    shopCodes,
    metiVersions,
    parentSocieties,
    metiShops,
    setShouldRefreshData,
    permissions
  } = props
  const { showNotification } = useNotification()
  const isManager = permissions?.canEdit && !permissions?.canAdd && isEdit

  const { data: depositsOptions } = useQuery(
    ['deposits'],
    async () => {
      const result = await makeCoreGetRequest(
        isEdit
          ? `/shop-reception/shops/${editItem?.metiCode}/deposits`
          : `/shop-reception/deposits/accessible`
      )

      const resultOptions = result.map((item: any): any => ({
        label: item.displayName,
        value: item.id,
        isChecked: item.isChecked
      }))
      return resultOptions || []
    },
    {
      onError: error => {
        showNotification({
          message: `${formatErrorMessage(error)}`,
          type: 'error'
        })
      },
      enabled: isOpen
    }
  )

  const { data: regionsOptions, isLoading: isLoadingRegionsOptions } = useQuery(
    ['regions-options', editItem?.id],
    async () => {
      const result = await makeCoreGetRequest(`/shop-reception/shop/${editItem?.id}/regions`)
      const options = result.map(item => ({ label: item.name, value: item.id }))
      return options
    },
    {
      onError: error => {
        showNotification({
          message: `${formatErrorMessage(error)}`,
          type: 'error'
        })
      },
      enabled: typeof editItem?.id !== 'undefined'
    }
  )

  const addShop = async values => {
    if (isEdit) {
      if (permissions?.canEdit && !permissions?.canAdd) {
        await makeCorePatchRequest(
          `/shop-reception/shops/reception-deviation/${editItem!.id}`,
          null,
          { params: { receptionDeviation: values.receptionDeviation } }
        )
      } else {
        await makeCorePatchRequest(`/shop-reception/shops/${editItem.id}`, values)
      }
    } else {
      await makeCorePostRequest('/shop-reception/shops', { ...values, receptionDeviation: 0 })
    }
  }

  const { isLoading, mutate } = useMutation(addShop, {
    onSuccess: () => {
      showNotification({
        message: `Magazinul a fost ${isEdit ? 'editat' : 'adaugat'} cu succes.`,
        type: 'success'
      })
      closeModal()
      formik.resetForm()
      setShouldRefreshData(true)
    },
    onError: res => {
      showNotification({
        message: `A aparut o eroare la ${
          isEdit ? 'editarea' : 'adaugarea'
        } magazinului. ${formatErrorMessage(res)}`,
        type: 'error'
      })
    }
  })

  const initialValues = useMemo(
    () => getInitialValues(editItem, depositsOptions, isEdit),
    [editItem, depositsOptions]
  )
  const validationSchema = useMemo(() => getValidationSchema(editItem), [editItem])
  const managerSchema = useMemo(() => getManagerValidationSchema(editItem), [editItem])

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: isManager ? managerSchema : validationSchema,
    onSubmit: async (values: any) => {
      const payload = {
        ...values,
        depositIds: values.depositIds.filter(item => item.isChecked).map(item => item.value),
        code: values.code.value,
        metiVersion: values.metiVersion.value,
        parentSociety: values.parentSociety.value
      }
      mutate(payload)
    }
  })

  if (permissions?.canEdit && !permissions?.canAdd && isEdit) {
    return (
      <Modal
        isModalOpen={isOpen}
        closeModal={closeModal}
        modalTitle={isEdit ? 'Editează magazin' : 'Adaugă magazin'}
        modalWidth="50%"
      >
        <Grid container spacing={2}>
          {editItem && (
            <Grid xs={12} item>
              <FormInfoItems
                items={[
                  ...(editItem ? [{ label: 'Magazin:', name: editItem?.metiName }] : []),
                  ...(!permissions?.canEditFields && editItem
                    ? [{ label: 'Versiune METI:', name: editItem?.metiVersion }]
                    : []),
                  ...(!permissions?.canEditFields && editItem
                    ? [{ label: 'Cod AD:', name: editItem?.adCode }]
                    : [])
                ]}
              />
            </Grid>
          )}

          {permissions?.canEditFields && (
            <Grid xs={12} item>
              <Autocomplete
                variant="outlined"
                label="Versiune METI"
                onChange={(event, option) => formik.setFieldValue('metiVersion', option)}
                value={formik.values.metiVersion}
                options={metiVersions || []}
                disabled
              />
            </Grid>
          )}

          {permissions?.canEditFields && (
            <Grid xs={12} item>
              <Input
                type="text"
                value={formik.values.adCode}
                label="Cod AD"
                variant="outlined"
                style={{ width: '100%' }}
                disabled
              />
            </Grid>
          )}

          <Grid xs={12} item>
            <Autocomplete
              variant="outlined"
              label="Societate"
              onChange={(event, option) => formik.setFieldValue('parentSociety', option)}
              value={formik.values.parentSociety}
              options={parentSocieties || []}
              disabled
            />
          </Grid>

          <Grid xs={12} item>
            <Input
              type="number"
              value={formik.values.receptionDeviation}
              onChange={event =>
                formik.setFieldValue('receptionDeviation', (event.target as HTMLInputElement).value)
              }
              label="Abatere recepţie produse (grame)"
              variant="outlined"
              style={{ width: '100%' }}
              error={!!formik.errors.receptionDeviation}
              helperText={formik.errors.receptionDeviation as string}
              required
            />
          </Grid>

          <>
            {isLoadingRegionsOptions ? (
              <Grid xs={12} item>
                <CircularProgress color="primary" disableShrink />
              </Grid>
            ) : (
              <Grid xs={12} item>
                <Autocomplete
                  variant="outlined"
                  label="Regiune"
                  value={regionsOptions}
                  options={[]}
                  multiple
                  disabled
                  loading={isLoadingRegionsOptions}
                />
              </Grid>
            )}
          </>

          <Grid xs={12} item>
            <Input
              type="text"
              value={formik.values.sftpIp}
              label="IP SFTP"
              variant="outlined"
              style={{ width: '100%' }}
              disabled
            />
          </Grid>

          <Grid xs={12} item>
            <Input
              type="text"
              value={formik.values.sftpPath}
              label="Cale SFTP"
              variant="outlined"
              style={{ width: '100%' }}
              disabled
            />
          </Grid>

          <Grid xs={12} item>
            <Input
              type="text"
              value={formik.values.metiGrappe}
              label="Meti Grappe"
              variant="outlined"
              style={{ width: '100%' }}
              disabled
            />
          </Grid>

          <Grid xs={12} item>
            <Autocomplete
              variant="outlined"
              label="Depozite asociate"
              value={formik.values.depositIds?.filter(item => item.isChecked === true)}
              options={formik.values.depositIds}
              multiple
              readOnly
            />
          </Grid>

          <Grid xs={12} item>
            <Input
              type="text"
              value={formik.values.address}
              label="Adresa"
              variant="outlined"
              style={{ width: '100%' }}
              disabled
            />
          </Grid>

          <Grid xs={12} item>
            <Input
              type="text"
              value={formik.values.city}
              label="Oras"
              variant="outlined"
              style={{ width: '100%' }}
              disabled
            />
          </Grid>

          <Grid xs={12} item>
            <Input
              type="text"
              value={formik.values.town}
              label="Judet"
              variant="outlined"
              style={{ width: '100%' }}
              disabled
            />
          </Grid>

          <Grid xs={12} item>
            <Checkbox
              checked={formik.values.canEditQuantities}
              defaultChecked={formik.values.canEditQuantities}
              label="Permite editare cantităţi recepţionate"
              color={!!formik.errors.active ? 'primary' : 'secondary'}
              disabled
            />
          </Grid>

          <Grid xs={12} item>
            <Checkbox
              checked={formik.values.canCallReturnTruck}
              defaultChecked={formik.values.canCallReturnTruck}
              label="Poate chema Camion de retur"
              color={!!formik.errors.active ? 'primary' : 'secondary'}
              disabled
            />
          </Grid>

          <Grid xs={12} item>
            <Checkbox
              checked={formik.values.active}
              label="Activ"
              color={!!formik.errors.active ? 'primary' : 'secondary'}
              disabled
            />
          </Grid>

          <Grid xs={12} item className={s.modalButtons}>
            <Button variant="outlined" label="Anuleaza" onClick={() => closeModal()} />
            <Button
              variant="contained"
              label={'Salveaza'}
              onClick={() => formik.handleSubmit()}
              disabled={isLoading}
            />
          </Grid>
        </Grid>
      </Modal>
    )
  }

  return (
    <Modal
      isModalOpen={isOpen}
      closeModal={closeModal}
      modalTitle={isEdit ? 'Editează magazin' : 'Adaugă magazin'}
      modalWidth="50%"
    >
      <Grid container spacing={2}>
        {editItem && (
          <Grid xs={12} item>
            <FormInfoItems
              items={[
                ...(editItem ? [{ label: 'Magazin:', name: editItem?.metiName }] : []),
                ...(!permissions?.canEditFields && editItem
                  ? [{ label: 'Versiune METI:', name: editItem?.metiVersion }]
                  : []),
                ...(!permissions?.canEditFields && editItem
                  ? [{ label: 'Cod AD:', name: editItem?.adCode }]
                  : [])
              ]}
            />
          </Grid>
        )}

        {!editItem && (
          <Grid xs={12} item>
            <Autocomplete
              variant="outlined"
              label="Nume magazin"
              disableClearable
              required
              onChange={(event, option) => formik.setFieldValue('code', option)}
              value={formik.values.code}
              options={metiShops || []}
              error={!!formik.errors.code}
              helperText={formik.errors.code as string}
            />
          </Grid>
        )}

        {(!editItem || permissions?.canEditFields) && (
          <Grid xs={12} item>
            <Autocomplete
              variant="outlined"
              label="Versiune METI"
              disableClearable
              required
              onChange={(event, option) => formik.setFieldValue('metiVersion', option)}
              value={formik.values.metiVersion}
              options={metiVersions || []}
              error={!!formik.errors.metiVersion}
              helperText={formik.errors.metiVersion as string}
            />
          </Grid>
        )}

        {(!editItem || permissions?.canEditFields) && (
          <Grid xs={12} item>
            <Input
              type="text"
              value={formik.values.adCode}
              onChange={event =>
                formik.setFieldValue('adCode', (event.target as HTMLInputElement).value)
              }
              label="Cod AD"
              variant="outlined"
              style={{ width: '100%' }}
              error={!!formik.errors.adCode}
              helperText={formik.errors.adCode as string}
              required
            />
          </Grid>
        )}

        <Grid xs={12} item>
          <Autocomplete
            variant="outlined"
            label="Societate"
            disableClearable
            required
            onChange={(event, option) => formik.setFieldValue('parentSociety', option)}
            value={formik.values.parentSociety}
            options={parentSocieties || []}
            error={!!formik.errors.parentSociety}
            helperText={formik.errors.parentSociety as string}
          />
        </Grid>

        <Grid xs={12} item>
          <Input
            type="number"
            label="Abatere recepţie produse (grame)"
            value={formik.values.receptionDeviation}
            variant="outlined"
            style={{ width: '100%' }}
            disabled
          />
        </Grid>

        {editItem && (
          <>
            {isLoadingRegionsOptions ? (
              <Grid xs={12} item>
                <CircularProgress color="primary" disableShrink />
              </Grid>
            ) : (
              <Grid xs={12} item>
                <Autocomplete
                  variant="outlined"
                  label="Regiune"
                  disableClearable
                  value={regionsOptions}
                  defaultValue={regionsOptions}
                  options={[]}
                  multiple
                  disabled
                  loading={isLoadingRegionsOptions}
                />
              </Grid>
            )}
          </>
        )}

        <Grid xs={12} item>
          <Input
            type="text"
            value={formik.values.sftpIp}
            onChange={event =>
              formik.setFieldValue('sftpIp', (event.target as HTMLInputElement).value)
            }
            label="IP SFTP"
            variant="outlined"
            style={{ width: '100%' }}
            error={!!formik.errors.sftpIp}
            helperText={formik.errors.sftpIp as string}
            required
          />
        </Grid>

        <Grid xs={12} item>
          <Input
            type="text"
            value={formik.values.sftpPath}
            onChange={event =>
              formik.setFieldValue('sftpPath', (event.target as HTMLInputElement).value)
            }
            label="Cale SFTP"
            variant="outlined"
            style={{ width: '100%' }}
            error={!!formik.errors.sftpPath}
            helperText={formik.errors.sftpPath as string}
            required
          />
        </Grid>

        <Grid xs={12} item>
          <Input
            type="text"
            value={formik.values.metiGrappe}
            onChange={event =>
              formik.setFieldValue('metiGrappe', (event.target as HTMLInputElement).value)
            }
            label="Meti Grappe"
            variant="outlined"
            style={{ width: '100%' }}
            error={!!formik.errors.metiGrappe}
            helperText={formik.errors.metiGrappe as string}
          />
        </Grid>

        {isEdit && (
          <Grid xs={12} item>
            <Autocomplete
              variant="outlined"
              label="Depozite asociate"
              value={formik.values.depositIds?.filter(item => item.isChecked === true)}
              options={formik.values.depositIds}
              multiple
              readOnly
            />
          </Grid>
        )}

        <Grid xs={12} item>
          <Input
            type="text"
            value={formik.values.address}
            onChange={event =>
              formik.setFieldValue('address', (event.target as HTMLInputElement).value)
            }
            label="Adresa"
            variant="outlined"
            style={{ width: '100%' }}
            error={!!formik.errors.address}
            helperText={formik.errors.address as string}
            required
          />
        </Grid>

        <Grid xs={12} item>
          <Input
            type="text"
            value={formik.values.city}
            onChange={event =>
              formik.setFieldValue('city', (event.target as HTMLInputElement).value)
            }
            label="Oras"
            variant="outlined"
            style={{ width: '100%' }}
            error={!!formik.errors.city}
            helperText={formik.errors.city as string}
            required
          />
        </Grid>

        <Grid xs={12} item>
          <Input
            type="text"
            value={formik.values.town}
            onChange={event =>
              formik.setFieldValue('town', (event.target as HTMLInputElement).value)
            }
            label="Judet"
            variant="outlined"
            style={{ width: '100%' }}
            error={!!formik.errors.town}
            helperText={formik.errors.town as string}
            required
          />
        </Grid>

        <Grid xs={12} item>
          <Checkbox
            checked={formik.values.canEditQuantities}
            defaultChecked={formik.values.canEditQuantities}
            onChange={() =>
              formik.setFieldValue('canEditQuantities', !formik.values.canEditQuantities)
            }
            label="Permite editare cantităţi recepţionate"
            color={!!formik.errors.active ? 'primary' : 'secondary'}
          />
        </Grid>

        <Grid xs={12} item>
          <Checkbox
            checked={formik.values.canCallReturnTruck}
            defaultChecked={formik.values.canCallReturnTruck}
            onChange={() =>
              formik.setFieldValue('canCallReturnTruck', !formik.values.canCallReturnTruck)
            }
            label="Poate chema Camion de retur"
            color={!!formik.errors.active ? 'primary' : 'secondary'}
          />
        </Grid>

        <Grid xs={12} item>
          <Checkbox
            checked={formik.values.active}
            onChange={() => formik.setFieldValue('active', !formik.values.active)}
            label="Activ"
            color={!!formik.errors.active ? 'primary' : 'secondary'}
          />
        </Grid>

        <Grid xs={12} item className={s.modalButtons}>
          <Button variant="outlined" label="Anuleaza" onClick={() => closeModal()} />
          <Button
            variant="contained"
            label={'Salveaza'}
            onClick={() => formik.handleSubmit()}
            disabled={isLoading}
          />
        </Grid>
      </Grid>
    </Modal>
  )
}

export default AdminStoreForm
